import { type Ref, watch, WatchHandle } from 'vue';
import type { TeamInfo, UserInfo } from '@/types';
import { PersonalSurveyProfile } from '@/types';
import { setupTechTouch } from '@/utils/techTouch';
import { integrateWithZendesk } from '@/utils/zendesk';

type OptionalIntegrations = {
  zendesk?: boolean;
};

const setup = (
  userInfo: UserInfo,
  teamInfo: TeamInfo,
  personalSurveyProfile?: PersonalSurveyProfile,
  optionalIntegrations?: OptionalIntegrations,
) => {
  setupTechTouch(userInfo, teamInfo, personalSurveyProfile);

  if (optionalIntegrations?.zendesk) {
    integrateWithZendesk();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).zE !== undefined) {
      // 別でhideされている可能性があるのでZendeskウィジェットを表示する
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).zE('webWidget', 'show');
    }
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).zE !== undefined) {
      // Zendeskウィジェットが存在していた場合は非表示にする
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).zE('webWidget', 'hide');
    }
  }
};

/**
 * サードパーティツール（Zendesk、TechTouch）との統合設定を行うcomposable
 */
export const useThirdPartyToolsSetup = ({
  userInfo,
  teamInfo,
  personalSurveyProfile,
  optionalIntegrations = {},
}: {
  userInfo: Ref<UserInfo | undefined>;
  teamInfo: Ref<TeamInfo | undefined>;
  personalSurveyProfile: Ref<PersonalSurveyProfile | undefined>;
  optionalIntegrations?: OptionalIntegrations;
}) => {
  // watch(immediate: true)だと、コールバック内が同期実行になることがあり、
  // そのときは、watchの返り値でunwatchの変数定義をしてしまうと、
  // コールバック内では未定義になってしまうため、事前に定義しておく
  let unwatch: WatchHandle | null = null;

  // watch内が同期で実行されたときに即座にunwatchするためのフラグ
  let shouldStop = false;

  unwatch = watch(
    [userInfo, teamInfo, personalSurveyProfile],
    ([u, t, psp]) => {
      if (u === undefined || t === undefined) return;
      const enablePersonalHint = t.enable_personal_survey_mail;

      // パーソナルヒントが有効なときだけpersonalSurveyProfileをチェックする
      if (enablePersonalHint && psp === undefined) return;

      setup(u, t, psp, optionalIntegrations);
      unwatch?.();
      shouldStop = true;
    },
    { immediate: true },
  );

  if (shouldStop) unwatch();
};
